import { useQuery } from "@tanstack/react-query";
import { FC, SVGProps } from "react";
import { Link } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { openUrl } from "@/utils/openUrl/openUrl";
import { getPlatform, getPlatformLocation } from "api/methods";
import Logo from "assets/images/RepeatMD_myRepeat.svg";
import { Icon } from "atoms/icon/Icon";
import Facebook from "atoms/icon/icons/facebook.svg";
import Instagram from "atoms/icon/icons/instagram.svg";
import Mail from "atoms/icon/icons/mail.svg";
import MapPin from "atoms/icon/icons/map_pin.svg";
import Phone from "atoms/icon/icons/phone.svg";
import TikTok from "atoms/icon/icons/tiktok.svg";
import Twitter from "atoms/icon/icons/twitter.svg";
import { formatAddressNoCountry } from "toolbox/Address";
import { formatPhoneNumber } from "toolbox/PhoneNumber";

import { usePrimaryNavItems } from "../usePrimaryNavItems";

const SocialIconLink = ({
  label,
  svg,
  url,
}: {
  label: string;
  svg: FC<SVGProps<SVGSVGElement>>;
  url: string;
}) => (
  <button
    aria-label={label}
    className="-ml-2.5 rounded-full p-2.5 transition-colors hover:bg-white/10"
    type="button"
    onClick={() => openUrl(url)}
  >
    <Icon svg={svg} size="xsmall" color="currentColor" />
  </button>
);

const ContactRow = ({
  svg,
  text,
  url,
}: {
  svg: FC<SVGProps<SVGSVGElement>>;
  text: string;
  url: string;
}) => (
  <button
    className="flex items-start gap-2 text-left text-body2 text-white/80 transition-colors hover:text-white"
    type="button"
    onClick={() => openUrl(url)}
  >
    <Icon svg={svg} size="x3small" color="currentColor" className="mt-0.5 shrink-0" />
    <span>{text}</span>
  </button>
);

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="text-body2 capitalize text-white/80 transition-colors hover:text-white">
    {children}
  </Link>
);

/**
 * Website-style footer for tablet and desktop.
 *
 * On phones the bottom tab bar is pinned to the viewport and a page never
 * really "ends", so there is nowhere for a footer to live. Once the tab bar is
 * gone the page does end, and it needs somewhere to land: practice identity,
 * how to reach them, and a second path to everything the masthead links to.
 */
export const SiteFooter = () => {
  const { t } = useTranslation();
  const navItems = usePrimaryNavItems();

  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { data: platformLocation } = useQuery({ ...getPlatformLocation.keyed() });

  const address = formatAddressNoCountry(
    platformLocation?.address1,
    platformLocation?.address2,
    platformLocation?.city,
    platformLocation?.state,
    platformLocation?.postalCode,
  );
  const phone = formatPhoneNumber(platformLocation?.phoneNumber);
  const email = platformLocation?.email;

  const settings = platform?.platformSettings;
  const shopLinks = settings?.marketplaceEnabled
    ? [
        (settings.treatmentsAndPackagesEnabled ?? true) && {
          to: "/shop/treatments",
          label: t("shopPage.treatments"),
        },
        (settings.membershipsEnabled ?? true) && {
          to: "/shop/memberships",
          label: t("shopPage.memberships"),
        },
        (settings.treatmentsAndPackagesEnabled ?? true) && {
          to: "/shop/packages",
          label: t("shopPage.packages"),
        },
        settings.giftCardEnabled && { to: "/shop/gift-cards", label: t("shopPage.giftCards") },
      ].filter((link): link is { to: string; label: string } => !!link)
    : [];

  return (
    <footer className="mt-auto hidden border-t border-white/15 bg-[#172A2D] text-white desk:block">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-10 px-6 py-12 desklg:grid-cols-4 desklg:px-8 desk2xl:max-w-6xl">
        <div className="col-span-2 flex flex-col gap-4">
          {platform?.logoImageUrl ? (
            <img
              src={platform.logoImageUrl}
              alt={platform.name ?? ""}
              className="h-12 w-auto max-w-48 self-start rounded bg-white p-2"
            />
          ) : (
            <div className="font-serif text-h3 text-white">{platform?.name}</div>
          )}

          <div className="flex max-w-sm flex-col gap-2">
            {!!address && (
              <ContactRow
                svg={MapPin}
                text={address}
                url={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              />
            )}
            {!!phone && <ContactRow svg={Phone} text={phone} url={`tel:${phone}`} />}
            {!!email && <ContactRow svg={Mail} text={email} url={`mailto:${email}`} />}
          </div>

          {(platform?.instagramUrl ||
            platform?.facebookUrl ||
            platform?.twitterUrl ||
            platform?.tikTokUrl) && (
            <div className="flex items-center gap-1">
              {platform.instagramUrl && (
                <SocialIconLink
                  label={t("campaignCard.social.instagram")}
                  svg={Instagram}
                  url={platform.instagramUrl}
                />
              )}
              {platform.facebookUrl && (
                <SocialIconLink
                  label={t("campaignCard.social.facebook")}
                  svg={Facebook}
                  url={platform.facebookUrl}
                />
              )}
              {platform.twitterUrl && (
                <SocialIconLink
                  label={t("campaignCard.social.x")}
                  svg={Twitter}
                  url={platform.twitterUrl}
                />
              )}
              {platform.tikTokUrl && (
                <SocialIconLink
                  label={t("campaignCard.social.tiktok")}
                  svg={TikTok}
                  url={platform.tikTokUrl}
                />
              )}
            </div>
          )}
        </div>

        <nav className="flex flex-col gap-3">
          {navItems.map((item) => (
            <FooterLink key={item.key} to={item.to}>
              {item.label}
            </FooterLink>
          ))}
        </nav>

        {shopLinks.length > 0 && (
          <nav className="flex flex-col gap-3">
            {shopLinks.map((link) => (
              <FooterLink key={link.to} to={link.to}>
                {link.label}
              </FooterLink>
            ))}
          </nav>
        )}
      </div>

      <div className="border-t border-white/15">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5 desklg:px-8 desk2xl:max-w-6xl">
          <div className="flex items-center gap-1.5">
            <span className="text-body3 text-white/70">{t("repeatmdFooter.poweredBy")}</span>
            <Logo className="fill-white" />
          </div>
          <div className="flex items-center gap-6">
            <button
              className="text-bold3 text-white/80 transition-colors hover:text-white"
              type="button"
              onClick={() => openUrl("https://myrepeat.com/terms.html")}
            >
              {t("repeatmdFooter.termsOfService")}
            </button>
            <button
              className="text-bold3 text-white/80 transition-colors hover:text-white"
              type="button"
              onClick={() => openUrl("https://myrepeat.com/privacy-policy.html")}
            >
              {t("repeatmdFooter.privacyPolicy")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
